<script lang="ts">
import { defineComponent, inject, onMounted, Ref, ref } from "vue";
import Logger from "js-logger";
import keycloakStore from "@/businesslogic/stores/keycloakStore";
import axios from "axios";
import { Datei } from "@/model/Datei";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "DateiUpload",
  components: {},
  props: {
    id: String,
    extensions: {
      type: String,
      default: ".jpeg,.jpg,.png,.pdf,.tiff",
    },
    fileSize: {
      type: Number,
      default: 10,
    },
    fileCount: {
      type: Number,
      default: 10,
    },
    ueberschrift: {
      type: String,
      default: "Dokumente hinzufügen",
    },
    ueberschriftDownloadLink: {
      type: String,
      default: "",
    },
    dokumentart: {
      type: String,
      default: "Foto",
    },
  },
  setup: (props) => {
    const piniaStore = useFormStore();
    const tempFiles = ref([]);
    const currentFileCount = ref(0);
    const fehlerListe: Ref<string[]> = ref([]);
    const maxFehler = ref(false);
    const zeigeSpinner: any = inject("zeigeSpinner");

    function getDateienZuGruppe(): Datei[] {
      const dateien: Datei[] = [];
      for (const datei of piniaStore.state.dateien) {
        if (datei.gruppe === props.id) {
          dateien.push(datei);
        }
      }
      return dateien;
    }

    onMounted(() => {
      currentFileCount.value = getDateienZuGruppe().length;
    });

    const http = () => {
      const keycloakAccessToken = keycloakStore().getKeycloakAccessToken();

      let headers;
      if (keycloakAccessToken) {
        headers = {
          Authorization: "Bearer " + keycloakAccessToken,
          "Content-Type": "application/json",
        };
      } else {
        headers = {
          "Content-Type": "application/json",
        };
      }

      return axios.create({
        baseURL: process.env.VUE_APP_BACKEND_URL,
        headers: headers,
      });
    };

    const convertFile = async (file: any, dokumentart: string | undefined, gruppe: string | undefined) => {
      const reader = new FileReader();
      return new Promise((resolve, reject) => {
        reader.readAsDataURL(file);
        reader.onload = function () {
          const neueDatei = {
            id: undefined,
            file: reader.result,
            name: file.name,
            size: file.size,
            type: file.type,
            state: "uploading",
            dokumentart: dokumentart,
            gruppe: gruppe,
          };

          resolve(neueDatei);
        };
        reader.onerror = function (error) {
          Logger.warn("Error: ", error);
          fehlerListe.value.push("Die Datei <strong>" + file.name + "</strong> konnte nicht verarbeitet werden.");
          reject(error);
        };
      });
    };

    const extensionGueltig = (filename: string) => {
      const endung = filename.split(".").pop();
      return !!(props.extensions && endung && endung !== filename && props.extensions.indexOf(endung) > -1);
    };

    const groesseGueltig = (filesize: number) => {
      if (!props.fileSize) {
        return true;
      }

      return filesize <= props.fileSize * 1048576;
    };

    const checkFile = (newFile: any) => {
      if (newFile) {
        Logger.debug("InputFilter", props.fileCount, currentFileCount.value, newFile);

        if (props.fileCount && currentFileCount.value >= props.fileCount) {
          if (maxFehler.value === false) {
            fehlerListe.value.push("Es sind maximal <strong>" + props.fileCount + "</strong> Dateien möglich.");
          }
          maxFehler.value = true;
          return false;
        }

        if (!extensionGueltig(newFile.name)) {
          fehlerListe.value.push("Die Datei <strong>" + newFile.name + "</strong> hat ein ungülitges Dateiformat.");
          return false;
        }
        if (!groesseGueltig(newFile.size)) {
          fehlerListe.value.push("Die Datei <strong>" + newFile.name + "</strong> ist zu groß.");
          return false;
        }
        currentFileCount.value++;
      }
      return true;
    };

    const uploadFile = async (file: any, dokumentart: string | undefined, gruppe: string | undefined) => {
      const data: any = await convertFile(file, dokumentart, gruppe);
      zeigeSpinner.value = true;

      http()
        .post("/api/makler/datei", data)
        .then((res) => {
          piniaStore.state.dateien.push(data);
          const index = piniaStore.state.dateien.length - 1;
          piniaStore.state.dateien[index].state = "success";
          piniaStore.state.dateien[index].file = "";
          piniaStore.state.dateien[index].id = res.data.id;
        })
        .catch((err) => {
          Logger.warn("Fehler beim Zufügen einer Datei", err);
          fehlerListe.value.push("Die Datei <strong>" + data.name + "</strong> konnte nicht verarbeitet werden.");
        })
        .finally(() => {
          zeigeSpinner.value = false;
        });

      return file;
    };

    const onInput = () => {
      fehlerListe.value = [];
      maxFehler.value = false;
    };

    const deleteFile = (index: number, id: string) => {
      onInput();
      currentFileCount.value--;
      http()
        .delete("/api/makler/datei/" + id)
        .then(() => {
          piniaStore.state.dateien.splice(index, 1);
        })
        .catch((err) => {
          Logger.warn("Fehler beim Löschen einer Datei", err);
        });
    };

    function vermeideDoppeldialog(event: Event) {
      if (event.isTrusted) {
        event.preventDefault();
      }
    }

    async function dateiauswahlVerarbeiten(event: any, dokumentart: string | undefined, gruppe: string | undefined) {
      if (event && event?.target && event?.target?.files) {
        for (const file of event.target.files) {
          if (checkFile(file)) {
            await uploadFile(file, dokumentart, gruppe);
          }
        }
      }
    }

    return {
      piniaStore,
      tempFiles,
      onInput,
      uploadFile,
      fehlerListe,
      deleteFile,
      currentFileCount,
      vermeideDoppeldialog,
      dateiauswahlVerarbeiten,
    };
  },
});
</script>

<template>
  <div class="upload u-mar-b-2">
    <h3 v-if="$props?.ueberschrift" class="ueberschrift">
      {{ $props.ueberschrift }}
      <span v-if="$props?.ueberschriftDownloadLink" class="ueberschriftDownloadLink">
        <a :href="$props.ueberschriftDownloadLink" target="_blank">herunterladen</a>
      </span>
    </h3>
    <div v-if="$props?.fileCount && $props.fileCount > currentFileCount" class="o-grid drop">
      <div class="o-grid__cell o-grid__cell--10"></div>
      <div class="o-grid__cell o-grid__cell--2 action">
        <div class="add" title="Datei hinzufügen">
          <label
            for="dateidialog"
            @click="($refs['dateidialog'] as any).click()"
            @keydown.enter.prevent="($refs['dateidialog'] as any).click()"
            @keydown.space.prevent="($refs['dateidialog'] as any).click()"
          >
            <img
              src="@/assets/images/icon-upload.svg"
              alt="Hochladen"
              title="Klicken, um eine Datei auszuwählen"
              class="icon"
            />
          </label>
          <input
            id="dateidialog"
            ref="dateidialog"
            :accept="$props.extensions ? $props.extensions : '*'"
            :multiple="$props.fileCount > 1"
            type="file"
            @click="vermeideDoppeldialog($event)"
            @change="dateiauswahlVerarbeiten($event, $props.dokumentart, $props.id)"
            @input="onInput"
            style="display: none"
          />
        </div>
      </div>
    </div>

    <div v-if="fehlerListe" class="gevng-form-validation__error-container">
      <div v-for="fehler in fehlerListe" :key="fehler" v-html="fehler"></div>
    </div>

    <template v-for="(file, index) in piniaStore.state.dateien" :key="file.id">
      <div v-if="file.gruppe === $props.id" class="o-grid list">
        <div class="o-grid__cell o-grid__cell--10 filename">
          {{ file.name }}
          <div v-if="file.state == 'error'" class="gevng-form-validation__error-container" style="margin-bottom: 10px">
            Datei konnte nicht hochgeladen werden - bitte probieren Sie es erneut.
          </div>
        </div>
        <div class="o-grid__cell o-grid__cell--2 action">
          <div @click="deleteFile(index, file.id)" class="delete" title="Datei löschen">
            <img
              src="@/assets/images/icon-trash.svg"
              alt="Löschen"
              title="Klicken, um eine Datei zu löschen"
              class="icon"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.o-grid__cell {
  margin: 7px !important;
}

@media (min-width: 360px), print {
  .o-grid > .o-grid__cell--2 {
    width: calc(16.66666% - 10px) !important;
  }
}

@media (min-width: 768px), print {
  .o-grid > .o-grid__cell--2 {
    width: calc(16.66666% - 7px) !important;
  }
}

.ueberschrift {
  font-weight: normal;
  margin-bottom: 0.75rem;
}

.ueberschriftDownloadLink {
  font-size: 0.85rem;
}

.gevng-form-validation__error-container {
  margin-bottom: 0 !important;
}

.drop {
  border: 1px dashed #0f5582;
  margin: 0 0 0.75rem 0;

  .text {
    padding-top: 0.4rem;
  }
}

.list {
  border: 1px solid #0f5582;
  margin: 0 0 0.75rem 0;

  .filename {
    padding-top: 0.25rem;
  }

  .size {
    padding-top: 0.25rem;
  }
}

.action {
  text-align: right;

  .delete,
  .add {
    cursor: pointer;

    .icon {
      height: 24px;
      width: 24px;
    }

    .icon:hover,
    .icon:active {
      cursor: pointer;
    }
  }

  .delete img {
    margin-right: 1px;
  }
}
</style>
